import type { ReactNode } from 'react'
import { Icon } from '@/components/Icon'
import { TableHeader, TableHost, ImageCell, DateCell } from '@/components/Table'
import { TableEmptyState } from '@/components/Table/TableEmptyState'
import {
  TableSearchbar,
  TableSearchbarMenuItem,
} from '@/components/TableSearchbar'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { Button } from '@/components/Button'
import { FilterControlsToggleButton } from '@/components/FilterControlsToggleButton'
import {
  ContextCell,
  AmountCell,
  IdentifierCell,
  FlagsCell,
} from '@/features/payouts/components/PayoutTransactionsTableCells'
import type { TransactionSourceType } from '@/features/payouts/transaction-source-type'
import type { UserTransactionReason } from '@/features/payouts/user-transaction-reason'
import type { TransactionFlag } from '@/features/payouts/transaction-flag'
import type { Id } from '@/infra/id'
import { byDesign } from '@/infra/design'
import imgSearchNoresultsUrl from '@/assets/misc/img_search-noresults.svg'
import SearchNoResultsIllustration from '@/components/SpotIllustration/SearchNoResultsIllustration'
import imgJobCompletedUrl from '@/assets/misc/img_job-completed.svg'
import CompleteJobIllustration from '@/components/SpotIllustration/CompleteJobIllustration'

export interface HistoricalTransactionsTableProps {
  searchText: string
  onSearchTextChange: (value: string) => unknown
  filtersShowing: boolean
  onToggleFilters: () => void
  appliedFilterCount: number
  isExportPending: boolean
  onExportCsv: () => unknown
  filterControls: ReactNode
  showTable: boolean
  appliedFilters: boolean
  onClearFilters: () => void
  children?: ReactNode
}

export function HistoricalTransactionsTable({
  searchText,
  onSearchTextChange,
  filtersShowing,
  onToggleFilters,
  appliedFilterCount,
  isExportPending,
  onExportCsv,
  filterControls,
  showTable,
  appliedFilters,
  onClearFilters,
  children,
}: HistoricalTransactionsTableProps) {
  return (
    <>
      <TableHost>
        <TableSearchbar
          searchText={searchText}
          onSearchTextChange={onSearchTextChange}
        >
          <TableSearchbarMenuItem>
            <FilterControlsToggleButton
              active={filtersShowing}
              count={appliedFilterCount}
              onPress={onToggleFilters}
            />
          </TableSearchbarMenuItem>
          <TableSearchbarMenuItem>
            <Dropdown
              autoDismiss
              button={<IconButton name="more" label="Transaction actions" />}
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                isDisabled={isExportPending}
                icon={<Icon name="download" />}
                onPress={onExportCsv}
              >
                <DropdownItemTitle>Download CSV</DropdownItemTitle>
              </DropdownItem>
            </Dropdown>
          </TableSearchbarMenuItem>
        </TableSearchbar>
        {filterControls}

        {showTable ? (
          children
        ) : (
          <HistoricalTransactionsEmptyState
            appliedFilters={appliedFilters}
            onClearFilters={onClearFilters}
          />
        )}
      </TableHost>
    </>
  )
}

export function HistoricalTransactionsTableHeader() {
  return (
    <>
      <TableHeader align="left">Context</TableHeader>
      <TableHeader align="left">Recipient</TableHeader>
      <TableHeader align="right">Amount</TableHeader>
      <TableHeader align="left">Workspace</TableHeader>
      <TableHeader align="left">Transaction short ID</TableHeader>
      <TableHeader align="left">Date completed</TableHeader>
      <TableHeader align="left">Stripe account ID</TableHeader>
      <TableHeader align="left">Flags</TableHeader>
    </>
  )
}

export interface HistoricalTransactionRowProps {
  id: Id
  source: TransactionSourceType
  memo: string | null
  reason: UserTransactionReason | null
  jobId: string | null
  recipientName: string
  recipientAvatar: string | null
  recipientAvatarFallback: string | null
  amount: number
  workspaceName: string
  workspaceIcon: string
  shortId: string
  completedAt: Date | null
  stripeAccountId: string | null
  flags: TransactionFlag[]
}

export function HistoricalTransactionRow({
  id,
  source,
  memo,
  reason,
  jobId,
  recipientName,
  recipientAvatar,
  recipientAvatarFallback,
  amount,
  workspaceName,
  workspaceIcon,
  shortId,
  completedAt,
  stripeAccountId,
  flags,
}: HistoricalTransactionRowProps) {
  return (
    <>
      <ContextCell
        source={source}
        userTransactionMemo={memo}
        userTransactionReason={reason}
        jobTransactionJobId={jobId}
      />
      <ImageCell
        title={recipientName}
        image={recipientAvatar}
        imageFallback={recipientAvatarFallback}
        minWidth={110}
      />
      <AmountCell amount={amount} />
      <ImageCell title={workspaceName} image={workspaceIcon} minWidth={110} />
      <IdentifierCell value={shortId} />
      <DateCell
        date={completedAt}
        testId={`historical-transaction-date-completed-${id}`}
      />
      <IdentifierCell value={stripeAccountId} />
      <FlagsCell flags={flags} />
    </>
  )
}

interface HistoricalTransactionsEmptyStateProps {
  appliedFilters: boolean
  onClearFilters: () => void
}

/**
 * Empty state.
 */
function HistoricalTransactionsEmptyState({
  appliedFilters,
  onClearFilters,
}: HistoricalTransactionsEmptyStateProps) {
  return (
    <TableEmptyState bordered>
      {appliedFilters ? (
        <EmptyState size="sm">
          <EmptyStateIllustration
            src={imgSearchNoresultsUrl}
            illustration={byDesign(undefined, SearchNoResultsIllustration)}
          />
          <EmptyStateTitle>
            We couldn't find what you were looking for.
          </EmptyStateTitle>
          <EmptyStateDescription>
            Maybe try{' '}
            <Button
              label="clearing your search filter"
              size="baseline"
              variant="text"
              onPress={onClearFilters}
            />
            ?
          </EmptyStateDescription>
        </EmptyState>
      ) : (
        <EmptyState size="sm">
          <EmptyStateIllustration
            src={imgJobCompletedUrl}
            illustration={byDesign(undefined, CompleteJobIllustration)}
          />
          <EmptyStateTitle>No transactions paid out yet.</EmptyStateTitle>
          <EmptyStateDescription>
            When you perform the first successful payout, the paid transactions
            will appear here.
          </EmptyStateDescription>
        </EmptyState>
      )}
    </TableEmptyState>
  )
}
